import type { IssueCreateEmbedSuccessPayload } from '@atlassian/gic-anywhere/types';

import { cfetch, BadStatusError } from '@confluence/network';

import {
	isGICCompatibleObject,
	isGICCompatibleObjectList,
	type CreateBulkIssuesProps,
	type CreateBulkIssuesResponse,
	type CreateIssueProps,
	type CreateIssueResponse,
	type FetchIssueTypeDataProps,
	type FetchProjectDataProps,
	type FieldMetadata,
	type GraphQLQuery,
	type GraphQLResponse,
	type IssueTypeAndId,
	type JiraAiEnabledResponse,
	type JiraComponents,
	type JiraIssueKeysToTypes,
	type JiraIssueMetadataResponse,
	type JiraIssueTypes,
	type JiraProjectType,
	type JiraProjects,
	type JiraVersions,
	type SupportedValueType,
	type TransitionResponseItems,
} from '../__types__/apiUtils.ts';
import type { AdditionalIssueField, IssueType } from '../providers/BulkCreateContextProvider.tsx';

const getJiraProjectsEndpoint = ({
	cloudId,
	query,
	projectIds,
	orderBy,
}: FetchProjectDataProps) => {
	let endpoint = `/gateway/api/ex/jira/${cloudId}/rest/api/3/project/search?action=create`;
	if (orderBy) {
		endpoint += `&orderBy=${orderBy}`;
	}
	if (projectIds && projectIds.length > 0) {
		endpoint += `&id=${projectIds.join('&id=')}`;
	}
	if (query && query.length > 0) {
		endpoint += `&query=${query}`;
	}
	return endpoint;
};

export const fetchJiraProjects = async ({
	cloudId,
	query,
	projectIds,
	orderBy,
}: FetchProjectDataProps): Promise<JiraProjects | undefined> => {
	try {
		const response = await cfetch(
			getJiraProjectsEndpoint({ cloudId, query, projectIds, orderBy }),
			{
				method: 'GET',
				headers: {
					Accept: 'application/json',
				},
				credentials: 'include',
			},
		);

		const data = await response.json();

		const projects = data?.values;

		if (!projects) {
			throw new Error('Parsing response from fetchJiraProjects failed');
		}

		return projects;
	} catch {
		throw new Error('Request to fetchJiraProjects agent failed');
	}
};

const getRecentJiraProjectsEndpoint = (cloudId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/project/recent`;

export const fetchRecentJiraProjects = async ({
	cloudId,
}: FetchProjectDataProps): Promise<JiraProjects | undefined> => {
	try {
		if (!cloudId) {
			throw new Error('cloudId is required');
		}
		const response = await cfetch(getRecentJiraProjectsEndpoint(cloudId), {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			credentials: 'include',
		});

		const recentProjects = await response.json();

		if (!recentProjects) {
			throw new Error('Parsing response from getRecentJiraProjects failed');
		}

		return recentProjects;
	} catch {
		throw new Error('Request to fetchRecentJiraProjects agent failed');
	}
};

export const getIssueTypesEndpoint = ({ cloudId, projectId }: FetchIssueTypeDataProps) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/issue/createmeta/${projectId}/issuetypes`;

export const fetchIssueTypes = async ({
	cloudId,
	projectId,
}: FetchIssueTypeDataProps): Promise<JiraIssueTypes | undefined> => {
	try {
		const response = await cfetch(getIssueTypesEndpoint({ cloudId, projectId }), {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			credentials: 'include',
		});

		const data = await response.json();

		const issues = data?.issueTypes;

		if (!issues) {
			throw new Error('Parsing response from fetchIssueTypes failed');
		}

		return issues;
	} catch {
		throw new Error('Request to fetchIssueTypes agent failed');
	}
};

export const getIssueCreateEndpoint = (cloudId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/issue`;

export const issueCreatePostRequest = ({
	cloudId,
	issueBody,
}: CreateIssueProps): Promise<Response> => {
	return cfetch(getIssueCreateEndpoint(cloudId), {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		body: JSON.stringify(issueBody),
	});
};

export const createIssue = async ({
	cloudId,
	issueBody,
}: CreateIssueProps): Promise<CreateIssueResponse | undefined> => {
	try {
		const response = await issueCreatePostRequest({ cloudId, issueBody });

		const data = await response.json();

		if (!data) {
			throw new Error('Parsing response from createIssue failed');
		}

		return data;
	} catch {
		throw new Error('Request to createIssue agent failed');
	}
};

export const getBulkIssueCreateEndpoint = (cloudId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/issue/bulk`;

export const createBulkIssues = async ({
	cloudId,
	issueBody,
}: CreateBulkIssuesProps): Promise<CreateBulkIssuesResponse | undefined> => {
	try {
		const response = await cfetch(getBulkIssueCreateEndpoint(cloudId), {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify(issueBody),
		});

		const data = await response.json();

		if (!data) {
			throw new Error('Parsing response from createBulkIssues failed');
		}

		return data;
	} catch {
		throw new Error('Request to createBulkIssues agent failed');
	}
};

export const promiseAllSettled = <T>(
	tasks: Array<() => Promise<T>>,
	limit: number,
): Promise<PromiseSettledResult<T>[]> => {
	return new Promise<Array<PromiseSettledResult<T>>>((resolve) => {
		const results: Array<PromiseSettledResult<T>> = [];
		if (tasks.length <= 0) return resolve(results);
		let currentIndex = 0;
		let activePromises = 0;
		const processNext = () => {
			while (activePromises < limit && currentIndex < tasks.length) {
				const index = currentIndex++;
				activePromises++;
				tasks[index]().then(
					(value) => handleSettlement(index, { status: 'fulfilled', value }),
					(reason) => handleSettlement(index, { status: 'rejected', reason }),
				);
			}
		};
		const handleSettlement = (index: number, result: PromiseSettledResult<T>) => {
			results[index] = result;
			activePromises--;
			if (currentIndex >= tasks.length && activePromises === 0) {
				resolve(results);
			} else {
				processNext();
			}
		};
		processNext();
	});
};

const getIssuesMetadataEndpoint = ({
	cloudId,
	projectId,
	issueTypeId,
	maxResults,
	startAt,
}: {
	cloudId: string;
	projectId: string;
	issueTypeId: string;
	maxResults: number;
	startAt: number;
}) => {
	const base = `/gateway/api/ex/jira/${cloudId}/rest/api/3/issue/createmeta/${projectId}/issuetypes/${issueTypeId}`;
	const withMaxResults = `${base}?maxResults=${maxResults}`;
	return `${withMaxResults}&startAt=${startAt}`;
};

export const fetchIssuesMetadata = async ({
	cloudId,
	projectId,
	issueTypeId,
	maxResults = 200,
	page = 0,
	consolidatedFields = [],
}: {
	cloudId: string;
	projectId: string;
	issueTypeId: string;
	maxResults?: number;
	page?: number;
	consolidatedFields?: FieldMetadata[];
}): Promise<JiraIssueMetadataResponse | undefined> => {
	try {
		const response = await cfetch(
			getIssuesMetadataEndpoint({
				cloudId,
				projectId,
				issueTypeId,
				maxResults,
				startAt: page * maxResults,
			}),
			{
				method: 'GET',
				headers: {
					Accept: 'application/json',
				},
				credentials: 'include',
			},
		);

		const data = await response.json();
		if (!data) {
			throw new Error('Parsing response from issues metadata failed');
		}

		const finalConsolidatedFields = [...consolidatedFields, ...data.fields];

		// The API can return a maximum result of 200. However, Issue types can sometimes have more than
		// 200 fields, in which case, the API may not be able to retrieve the summary and description fields correctly.
		// Therefore we call this API until the number of consolidated fields is equal to the value of 'total' indicated in the API response.
		// This 'total' indicates the total number of fields in all pages.
		if (data.total > finalConsolidatedFields.length) {
			return await fetchIssuesMetadata({
				cloudId,
				projectId,
				issueTypeId,
				maxResults,
				page: page + 1,
				consolidatedFields: finalConsolidatedFields,
			});
		}

		return {
			fields: finalConsolidatedFields,
			maxResults: data.maxResults,
			total: data.total,
			startAt: data.startAt,
		};
	} catch {
		throw new Error('Error retrieving issue metadata');
	}
};

// Util to map a successful issue create payload returned from the GIC to a CreateIssueResponse
export const mapGICPayloadToCreateIssueResponse = (
	payload: IssueCreateEmbedSuccessPayload,
): CreateIssueResponse => {
	return {
		id: payload.issueId ? payload.issueId.toString() : '',
		key: payload.issueKey,
		self: payload.link,
		transition: {
			status: 200,
			errorCollection: {
				errorMessages: [],
				errors: {},
			},
		},
		summary: payload.summary,
	};
};

// Converts a field value to a format that is compatible with the GIC API.
export const convertToGICFieldValue = (field: AdditionalIssueField): SupportedValueType => {
	if (isGICCompatibleObject(field.value)) {
		return field.value.id;
	}

	if (isGICCompatibleObjectList(field.value)) {
		return field.value.map((value) => value.id);
	}

	// For all other primitive cases, we will just use the value directly.
	return field.value;
};

export const getJiraAiEnablementEndpoint = (cloudId: string, jiraProduct: JiraProjectType) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/1/aiconfig/ai-enabled/${jiraProduct}`;

export const getJiraAiEnablementRequest = (
	cloudId: string,
	jiraProduct: JiraProjectType,
): Promise<Response> => {
	return cfetch(getJiraAiEnablementEndpoint(cloudId, jiraProduct), {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		credentials: 'include',
	});
};

export const getJiraAiEnablement = async (
	cloudId: string,
	jiraProduct: JiraProjectType,
): Promise<JiraAiEnabledResponse | undefined> => {
	try {
		const response = await getJiraAiEnablementRequest(cloudId, jiraProduct);
		const data = await response.json();

		if (!data) {
			throw new Error('Parsing response from getJiraAiEnablementRequest failed');
		}

		return data;
	} catch {
		throw new Error('Request to getJiraAiEnablementRequest agent failed');
	}
};

const getBulkWorkItemTypesEndpoint = (cloudId: string, workItemIds: string[]) => {
	const searchTerms = encodeURI(`issuekey in (${workItemIds.join(',')})`);
	return `/gateway/api/ex/jira/${cloudId}/rest/api/3/search?jql=${searchTerms}&fields=issuetype`;
};

// Fetches issue types for a list of Jira issues by their keys
export const fetchBulkWorkItemTypes = async ({
	cloudId,
	workItemKeys,
}: {
	cloudId: string;
	workItemKeys: string[];
}): Promise<JiraIssueKeysToTypes | undefined> => {
	try {
		const response = await cfetch(getBulkWorkItemTypesEndpoint(cloudId, workItemKeys), {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			credentials: 'include',
		});

		const data = await response.json();

		if (!data || !data.issues) {
			throw new Error('Parsing response from fetchBulkIssueTypes failed');
		}

		const issueTypeMap: JiraIssueKeysToTypes = new Map<string, IssueTypeAndId>();
		data.issues.forEach((issue: any) => {
			if (issue.key && issue.fields?.issuetype) {
				issueTypeMap.set(issue.key, {
					issueType: issue.fields.issuetype as IssueType,
					id: issue.id,
				});
			}
		});

		return issueTypeMap;
	} catch (error: Error | unknown) {
		// Return an empty map if the request fails due to a bad request, which should only happen if the work item keys are invalid.
		if (error instanceof BadStatusError && error.response.status === 400) {
			return new Map<string, IssueTypeAndId>();
		}
		throw new Error('Request to fetchBulkIssueTypes agent failed');
	}
};

const getComponentsEndpoint = (cloudId: string, projectId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/project/${projectId}/components`;

export const fetchJiraComponents = async ({
	cloudId,
	projectId,
}: {
	cloudId: string;
	projectId: string;
}): Promise<JiraComponents | null> => {
	try {
		const response = await cfetch(getComponentsEndpoint(cloudId, projectId), {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			credentials: 'include',
		});
		const data = await response.json();
		if (!data) {
			throw new Error('Parsing response from fetchComponents failed');
		}
		return data as JiraComponents;
	} catch {
		throw new Error('Request to fetchComponents agent failed');
	}
};

const getVersionsEndpoint = (cloudId: string, projectId: string) =>
	`/gateway/api/ex/jira/${cloudId}/rest/api/3/project/${projectId}/versions`;

export const fetchJiraVersions = async ({
	cloudId,
	projectId,
}: {
	cloudId: string;
	projectId: string;
}): Promise<JiraVersions | null> => {
	try {
		const response = await cfetch(getVersionsEndpoint(cloudId, projectId), {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			credentials: 'include',
		});
		const data = await response.json();
		if (!data) {
			throw new Error('Parsing response from fetchVersions failed');
		}
		return data as JiraVersions;
	} catch {
		throw new Error('Request to fetchVersions agent failed');
	}
};

const getWorkflowValidatorsEndpoint = (cloudId: string) =>
	`/gateway/api/jira/project-configuration/query/${cloudId}/2/transition/initial?expand=fieldRequiredValidators`;

export const fetchWorkflowValidators = async ({
	cloudId,
	projectId,
	issueTypeId,
}: {
	cloudId: string;
	projectId: string;
	issueTypeId: string;
}): Promise<TransitionResponseItems | undefined> => {
	try {
		const response = await cfetch(getWorkflowValidatorsEndpoint(cloudId), {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				projectId,
				issueTypeId,
			}),
		});
		const data = await response.json();
		if (!data) {
			throw new Error('Parsing response from fetchWorkflowValidators failed');
		}
		return data as TransitionResponseItems;
	} catch (error) {
		if (error instanceof Error && error.message.includes('Parsing response')) {
			throw error;
		}
		if (BadStatusError.isBadStatusError(error)) {
			const status = error.response.status;
			throw new Error(
				`Request to fetchWorkflowValidators failed with status ${status}: ${error.message}`,
			);
		}
		if (error instanceof SyntaxError) {
			throw new Error(`Invalid JSON response from fetchWorkflowValidators: ${error.message}`);
		}
		const errorMessage = error instanceof Error ? error.message : 'Unknown error';
		throw new Error(`Request to fetchWorkflowValidators agent failed: ${errorMessage}`);
	}
};

/**
 * Execute a GraphQL query using the correct Jira GraphQL endpoint through the Confluence gateway
 * @param query - The GraphQL query object containing query string, variables, and operation name
 * @param cloudId - The Jira cloud ID to route the request to
 * @returns Promise resolving to the GraphQL response
 */
export const executeGraphQLQuery = async <T = any>(
	query: GraphQLQuery,
	cloudId: string,
): Promise<GraphQLResponse<T>> => {
	const url = `/gateway/api/ex/jira/${cloudId}/rest/gira/1/?operation=${encodeURIComponent(query.operationName)}`;

	const requestBody = {
		query: query.query,
		variables: query.variables,
		operationName: query.operationName,
	};

	try {
		const response = await cfetch(url, {
			method: 'POST',
			body: JSON.stringify(requestBody),
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
		});

		const data = await response.json();

		if (!data) {
			throw new Error('Parsing response from GraphQL query failed');
		}

		return data;
	} catch {
		throw new Error('Request to GraphQL endpoint failed');
	}
};

export const ParentCandidatesForNewIssueQuery: string = `
	query ParentCandidatesForNewIssueQuery(
		$projectId: ID!
		$issueTypeId: ID!
		$cloudId: ID!
		$searchTerm: String
	) {
		jira {
			parentCandidatesWithInfoForNewIssue(
				projectId: $projectId
				issueTypeId: $issueTypeId
				cloudId: $cloudId
				searchTerm: $searchTerm
				excludeDone: false
				first: 1
			) {
				parentCandidates {
					edges {
						node {
							key
							issueId
						}
					}
				}
				message
			}
		}
	}
`;
